import { AudioChunk, AudioFormat } from '../types/audio';

export interface ChunkingConfig {
  /** Target chunk size in milliseconds */
  chunkSizeMs: number;
  /** Maximum chunk size in bytes */
  maxChunkSizeBytes?: number;
  /** Minimum chunk size in bytes */
  minChunkSizeBytes?: number;
}

/**
 * Service for managing audio chunking for real-time streaming
 * Handles buffering, timing, and sequence management
 */
export class AudioChunker {
  private buffer: ArrayBuffer[] = [];
  private sequenceNumber: number = 0;
  private lastChunkTime: number = 0;
  private config: ChunkingConfig;
  private format: AudioFormat;
  private onChunk?: (chunk: AudioChunk) => void;

  constructor(config: ChunkingConfig, format: AudioFormat, onChunk?: (chunk: AudioChunk) => void) {
    this.config = {
      maxChunkSizeBytes: 8192, // 8KB default max
      minChunkSizeBytes: 1024, // 1KB default min
      ...config,
    };
    this.format = format;
    this.onChunk = onChunk;
    this.lastChunkTime = Date.now();
  }

  /**
   * Add audio data to the chunker
   */
  addAudioData(audioData: ArrayBuffer): void {
    this.buffer.push(audioData);
    
    const currentTime = Date.now();
    const timeSinceLastChunk = currentTime - this.lastChunkTime;
    
    // Check if we should emit a chunk based on time or size
    if (this.shouldEmitChunk(timeSinceLastChunk)) {
      this.emitChunk();
    }
  }

  /**
   * Force emit any remaining buffered data as a chunk
   */
  flush(): void {
    if (this.buffer.length > 0) {
      this.emitChunk();
    }
  }

  /**
   * Reset the chunker state
   */
  reset(): void {
    this.buffer = [];
    this.sequenceNumber = 0;
    this.lastChunkTime = Date.now();
  }

  /**
   * Get current buffer size in bytes
   */
  getBufferSize(): number {
    return this.buffer.reduce((total, chunk) => total + chunk.byteLength, 0);
  }

  /**
   * Calculate expected chunk size in bytes based on format and timing
   */
  getExpectedChunkSize(): number {
    const bytesPerSample = this.format.bitsPerSample / 8;
    const samplesPerSecond = this.format.sampleRate * this.format.channels;
    const samplesPerChunk = (samplesPerSecond * this.config.chunkSizeMs) / 1000;
    return Math.floor(samplesPerChunk * bytesPerSample);
  }

  /**
   * Determine if a chunk should be emitted
   */
  private shouldEmitChunk(timeSinceLastChunk: number): boolean {
    const bufferSize = this.getBufferSize();
    const expectedChunkSize = this.getExpectedChunkSize();
    
    // Emit if time threshold reached
    if (timeSinceLastChunk >= this.config.chunkSizeMs) {
      return bufferSize >= (this.config.minChunkSizeBytes || 0);
    }
    
    // Emit if buffer is getting too large
    if (this.config.maxChunkSizeBytes && bufferSize >= this.config.maxChunkSizeBytes) {
      return true;
    }
    
    // Emit if we have approximately the expected amount of data
    if (bufferSize >= expectedChunkSize * 0.9) { // 90% threshold for some flexibility
      return true;
    }
    
    return false;
  }

  /**
   * Emit a chunk from the current buffer
   */
  private emitChunk(): void {
    if (this.buffer.length === 0) return;

    // Combine all buffered data into a single ArrayBuffer
    const totalSize = this.getBufferSize();
    const combinedBuffer = new ArrayBuffer(totalSize);
    const combinedView = new Uint8Array(combinedBuffer);
    
    let offset = 0;
    for (const chunk of this.buffer) {
      const chunkView = new Uint8Array(chunk);
      combinedView.set(chunkView, offset);
      offset += chunk.byteLength;
    }

    // Create the audio chunk
    const audioChunk: AudioChunk = {
      data: combinedBuffer,
      timestamp: Date.now(),
      sequenceNumber: this.sequenceNumber++,
    };

    // Clear the buffer
    this.buffer = [];
    this.lastChunkTime = Date.now();

    // Emit the chunk
    if (this.onChunk) {
      this.onChunk(audioChunk);
    }
  }

  /**
   * Create optimal chunking configuration based on audio format
   */
  static createOptimalConfig(format: AudioFormat, targetLatencyMs: number = 100): ChunkingConfig {
    // Calculate chunk size for target latency
    const chunkSizeMs = Math.min(targetLatencyMs, 200); // Max 200ms chunks
    
    // Calculate expected bytes per chunk
    const bytesPerSample = format.bitsPerSample / 8;
    const samplesPerSecond = format.sampleRate * format.channels;
    const expectedBytesPerChunk = (samplesPerSecond * chunkSizeMs * bytesPerSample) / 1000;
    
    return {
      chunkSizeMs,
      maxChunkSizeBytes: Math.floor(expectedBytesPerChunk * 2), // 2x expected size as max
      minChunkSizeBytes: Math.floor(expectedBytesPerChunk * 0.5), // 50% of expected as min
    };
  }

  /**
   * Validate chunking configuration
   */
  static validateConfig(config: ChunkingConfig, format: AudioFormat): { valid: boolean; error?: string } {
    if (config.chunkSizeMs <= 0 || config.chunkSizeMs > 1000) {
      return {
        valid: false,
        error: 'Chunk size must be between 1ms and 1000ms',
      };
    }

    if (config.maxChunkSizeBytes && config.minChunkSizeBytes) {
      if (config.maxChunkSizeBytes <= config.minChunkSizeBytes) {
        return {
          valid: false,
          error: 'Maximum chunk size must be greater than minimum chunk size',
        };
      }
    }

    // Check if chunk size is reasonable for the audio format
    const bytesPerSample = format.bitsPerSample / 8;
    const samplesPerSecond = format.sampleRate * format.channels;
    const expectedBytesPerChunk = (samplesPerSecond * config.chunkSizeMs * bytesPerSample) / 1000;
    
    if (config.minChunkSizeBytes && config.minChunkSizeBytes > expectedBytesPerChunk) {
      return {
        valid: false,
        error: `Minimum chunk size (${config.minChunkSizeBytes} bytes) is larger than expected chunk size (${expectedBytesPerChunk} bytes) for the given audio format`,
      };
    }

    return { valid: true };
  }
}

/**
 * Utility functions for audio chunk management
 */
export class AudioChunkUtils {
  /**
   * Calculate the duration of an audio chunk in milliseconds
   */
  static getChunkDuration(chunk: AudioChunk, format: AudioFormat): number {
    const bytesPerSample = format.bitsPerSample / 8;
    const totalSamples = chunk.data.byteLength / (bytesPerSample * format.channels);
    return (totalSamples / format.sampleRate) * 1000;
  }

  /**
   * Validate that audio chunks are in correct sequence
   */
  static validateSequence(chunks: AudioChunk[]): { valid: boolean; gaps?: number[]; duplicates?: number[] } {
    if (chunks.length === 0) return { valid: true };

    const gaps: number[] = [];
    const duplicates: number[] = [];
    const seen = new Set<number>();

    // Sort chunks by sequence number
    const sortedChunks = [...chunks].sort((a, b) => a.sequenceNumber - b.sequenceNumber);

    for (let i = 0; i < sortedChunks.length; i++) {
      const current = sortedChunks[i];
      
      // Check for duplicates
      if (seen.has(current.sequenceNumber)) {
        duplicates.push(current.sequenceNumber);
      } else {
        seen.add(current.sequenceNumber);
      }

      // Check for gaps (except for the first chunk)
      if (i > 0) {
        const previous = sortedChunks[i - 1];
        const expectedSequence = previous.sequenceNumber + 1;
        if (current.sequenceNumber !== expectedSequence) {
          // Record all missing sequence numbers
          for (let seq = expectedSequence; seq < current.sequenceNumber; seq++) {
            gaps.push(seq);
          }
        }
      }
    }

    return {
      valid: gaps.length === 0 && duplicates.length === 0,
      gaps: gaps.length > 0 ? gaps : undefined,
      duplicates: duplicates.length > 0 ? duplicates : undefined,
    };
  }

  /**
   * Merge multiple audio chunks into a single chunk
   */
  static mergeChunks(chunks: AudioChunk[]): AudioChunk | null {
    if (chunks.length === 0) return null;
    if (chunks.length === 1) return chunks[0];

    // Sort by sequence number
    const sortedChunks = [...chunks].sort((a, b) => a.sequenceNumber - b.sequenceNumber);

    // Calculate total size
    const totalSize = sortedChunks.reduce((sum, chunk) => sum + chunk.data.byteLength, 0);

    // Create merged buffer
    const mergedBuffer = new ArrayBuffer(totalSize);
    const mergedView = new Uint8Array(mergedBuffer);

    let offset = 0;
    for (const chunk of sortedChunks) {
      const chunkView = new Uint8Array(chunk.data);
      mergedView.set(chunkView, offset);
      offset += chunk.data.byteLength;
    }

    return {
      data: mergedBuffer,
      timestamp: sortedChunks[0].timestamp, // Use timestamp of first chunk
      sequenceNumber: sortedChunks[0].sequenceNumber, // Use sequence number of first chunk
    };
  }
}